import { T } from "../utils/theme";

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@300;400;500;600;700&family=Exo+2:wght@400;500;600;700;800;900&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    html, body, #root {
      height: 100%;
      background: ${T.bg};
      color: ${T.text};
      font-family: ${T.mono};
      font-size: 14px;
      line-height: 1.5;
      -webkit-font-smoothing: antialiased;
    }

    /* Scrollbar */
    ::-webkit-scrollbar { width: 5px; height: 5px; }
    ::-webkit-scrollbar-track { background: ${T.bg}; }
    ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
    ::-webkit-scrollbar-thumb:hover { background: ${T.muted}; }

    /* Inputs */
    input, textarea, select {
      font-family: ${T.mono};
      font-size: 13px;
      color: ${T.text};
      background: ${T.surface};
      border: 1px solid ${T.border};
      border-radius: 9px;
      padding: 10px 14px;
      width: 100%;
      outline: none;
      transition: border-color .15s, box-shadow .15s;
    }
    input:focus, textarea:focus, select:focus {
      border-color: ${T.blue};
      box-shadow: 0 0 0 3px ${T.blueGlow};
    }
    input::placeholder, textarea::placeholder { color: ${T.muted}; }
    select option { background: ${T.card}; }

    /* Animations */
    @keyframes fadeUp   { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:none } }
    @keyframes fadeIn   { from { opacity:0 } to { opacity:1 } }
    @keyframes spin     { to { transform: rotate(360deg) } }
    @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:.4} }
    @keyframes slideIn  { from { transform:translateX(-100%) } to { transform:none } }
    @keyframes glow     { 0%,100%{box-shadow:0 0 8px ${T.blue}50} 50%{box-shadow:0 0 20px ${T.blue}90} }
    @keyframes scanline { from{top:-100%} to{top:100%} }

    .fade-up   { animation: fadeUp  .35s ease both; }
    .fade-in   { animation: fadeIn  .25s ease both; }
    .spin      { animation: spin    .7s linear infinite; }

    /* Utility hover classes */
    .hov-card:hover {
      border-color: ${T.borderHov} !important;
      background: ${T.cardHov} !important;
      transform: translateY(-1px);
      transition: all .18s;
    }
    .hov-row:hover { background: rgba(79,142,247,0.04) !important; }
    .hov-nav:hover { background: rgba(79,142,247,0.08) !important; }

    /* Button base */
    .btn {
      display: inline-flex; align-items: center; gap: 7px;
      border: none; border-radius: 9px; padding: 9px 16px;
      font-family: ${T.mono}; font-size: 12px; font-weight: 600;
      cursor: pointer; transition: all .15s; white-space: nowrap;
      letter-spacing: .02em;
    }
    .btn-primary { background: ${T.blue}; color: #fff; }
    .btn-primary:hover { background: ${T.blueD}; box-shadow: 0 4px 16px ${T.blueGlow}; }
    .btn-ghost { background: ${T.card}; color: ${T.textSub}; border: 1px solid ${T.border}; }
    .btn-ghost:hover { border-color: ${T.blue}; color: ${T.text}; }
    .btn-success { background: rgba(0,230,118,.12); color: ${T.green}; border: 1px solid rgba(0,230,118,.3); }
    .btn-success:hover { background: rgba(0,230,118,.22); }
    .btn-danger { background: rgba(255,61,90,.1); color: ${T.red}; border: 1px solid rgba(255,61,90,.3); }
    .btn-danger:hover { background: rgba(255,61,90,.2); }
    .btn-warning { background: rgba(255,179,0,.1); color: ${T.yellow}; border: 1px solid rgba(255,179,0,.3); }
    .btn-warning:hover { background: rgba(255,179,0,.2); }
    .btn:disabled { opacity: .45; cursor: not-allowed; }
    .btn-sm { padding: 6px 12px; font-size: 11px; border-radius: 7px; }
    .btn-lg { padding: 12px 22px; font-size: 14px; border-radius: 11px; }
    .btn-icon { padding: 8px; border-radius: 8px; }

    /* Badge */
    .badge {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 3px 9px; border-radius: 5px;
      font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase;
    }

    /* Modal overlay */
    .modal-overlay {
      position: fixed; inset: 0;
      background: rgba(4,6,16,.82);
      backdrop-filter: blur(6px);
      z-index: 1000;
      display: flex; align-items: center; justify-content: center;
      animation: fadeIn .2s ease;
    }
    .modal-box {
      background: ${T.surface};
      border: 1px solid ${T.border};
      border-radius: 18px;
      padding: 28px 32px;
      max-width: 95vw;
      max-height: 90vh;
      overflow-y: auto;
      animation: fadeUp .25s ease;
    }

    /* Code block */
    .code-block {
      background: ${T.bg}; border: 1px solid ${T.border};
      border-radius: 8px; padding: 12px 16px;
      font-family: ${T.mono}; font-size: 11px;
      color: ${T.green}; word-break: break-all;
      overflow-y: auto; max-height: 120px;
    }

    /* Table */
    .data-table { width: 100%; border-collapse: collapse; }
    .data-table th {
      font-size: 9px; font-weight: 700; letter-spacing: .1em;
      color: ${T.muted}; padding: 10px 16px;
      border-bottom: 1px solid ${T.border}; text-align: left;
    }
    .data-table td {
      padding: 12px 16px; font-size: 12px;
      border-bottom: 1px solid ${T.border}22;
      vertical-align: middle;
    }
    .data-table tr:hover td { background: rgba(79,142,247,.03); }

    /* Tag pill */
    .tag {
      display: inline-flex; align-items: center;
      background: rgba(79,142,247,.12); color: ${T.blue};
      border: 1px solid rgba(79,142,247,.2);
      border-radius: 4px; padding: 2px 7px;
      font-size: 10px; margin: 2px;
    }

    /* Divider */
    .divider { height: 1px; background: ${T.border}; margin: 16px 0; }

    /* Grid helpers */
    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
    .grid-4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }

    /* Tooltip */
    .recharts-tooltip-wrapper .recharts-default-tooltip {
      background: ${T.card} !important;
      border: 1px solid ${T.border} !important;
      border-radius: 10px !important;
      font-family: ${T.mono} !important;
      font-size: 12px !important;
      box-shadow: 0 8px 24px rgba(0,0,0,.4) !important;
    }
  `}</style>
);

export default GlobalStyles;
